import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlusCircleOutlined } from '@ant-design/icons';

import { useAntMessage } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { Button } from '@/shared/ui/button';

import { useCreatePatternWatchlistMutation } from '../../api/pattern-watchlist.api';
import type { FormValues } from '../pattern-watchlist-form/pattern-watchlist-form';
import { PatternWatchlistModal } from '../pattern-watchlist-modal/pattern-watchlist-modal';

import cls from './create-pattern-watchlist.module.css';

export const CreatePatternWatchlist = memo(() => {
    const { t } = useTranslation('pattern-watchlist');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [createWatchlist, { isLoading, isSuccess }] =
        useCreatePatternWatchlistMutation();
    const { success } = useAntMessage();

    const onOpenModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const onCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const onSubmit = useCallback(
        async (values: FormValues) => {
            try {
                await createWatchlist({ name: values.name });

                success?.({
                    content: t('createSuccess'),
                });

                onCloseModal();
            } catch (error) {
                logError('Failed to create watchlist:', error);
            }
        },
        [createWatchlist, onCloseModal, success, t],
    );

    return (
        <>
            <Button
                className={cls.root}
                icon={<PlusCircleOutlined />}
                onClick={onOpenModal}
                title={t('createNewWatchlist')}
                variant="ghost"
            />

            <PatternWatchlistModal
                isLoading={isLoading}
                isOpen={isModalOpen}
                isSuccess={isSuccess}
                onClose={onCloseModal}
                onSubmit={onSubmit}
            />
        </>
    );
});

CreatePatternWatchlist.displayName = 'CreatePatternWatchlist';
